import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtINR } from "../../lib/constants";
import { toast } from "sonner";
import { BadgeIndianRupee, Gift, Star, Hammer, ShoppingBag, Heart, ShoppingCart, Sparkles, BookOpen, MapPin, FileText, MessageCircle, RefreshCcw, LogOut, User, Coins } from "lucide-react";

/**
 * User Dashboard — the customer's own dashboard: insights (LTV, loyalty,
 * referral code + earnings), daily rates, full buying history, rate locks,
 * scheme passbooks, karigar workflow tracking (status only — karigar identity
 * hidden), personal catalogs generated by them, blogs, stores, wishlist/cart
 * shortcuts, budget matcher and astrologer entry points, and a profile section
 * where email and phone can each be changed exactly once in a lifetime.
 */
const KARIGAR_STEPS = {
  design: ["received", "designing", "welding", "polishing", "finished", "delivered"],
  redesign: ["received", "designing", "welding", "polishing", "redesigning", "finished", "delivered"],
};

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [stores, setStores] = useState([]);
  const [edit, setEdit] = useState(null); // "email" | "phone"
  const [editVal, setEditVal] = useState("");
  const [goldScheme, setGoldScheme] = useState([]);
  const [credit, setCredit] = useState(null);

  const load = () => api.get("/api/me/overview").then((r) => {
    setData(r.data);
    const ph = (r.data?.customer?.phone || user?.phone || "").replace(/\D/g, "").slice(-10);
    if (ph) api.get(`/api/inventory/credits/mine?phone=${ph}`).then((c) => setCredit(c.data)).catch(() => {});
  }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/api/blogs").then((r) => setBlogs((r.data || []).slice(0, 3))).catch(() => {});
    api.get("/api/stores").then((r) => setStores(r.data || [])).catch(() => {});
    api.get("/api/gold-scheme/mine").then((r) => setGoldScheme(r.data || [])).catch(() => {});
  }, []);

  if (!user) return (
    <StorefrontLayout>
      <div className="py-24 text-center">
        <div className="font-display text-3xl text-maroon">Please log in to see your dashboard</div>
        <Link to="/login" className="btn-maroon inline-flex mt-4">Login</Link>
      </div>
    </StorefrontLayout>
  );

  const saveProfile = async () => {
    try {
      await api.post("/api/me/profile-change", edit === "email" ? { email: editVal } : { phone: editVal });
      toast.success(`${edit === "email" ? "Email" : "Phone"} updated — this was your one-time change`);
      setEdit(null); setEditVal(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
    navigate("/");
  };

  const d = data || {};
  const rates = d.rates || {};
  const exchanges = (d.orders || []).filter((o) => (o.notes || "").toLowerCase().includes("exchange") || o.is_exchange);

  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-10 max-w-7xl mx-auto space-y-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="eyebrow">My Space</div>
            <h1 className="font-display text-4xl lg:text-5xl text-maroon">Namaste, {d.profile?.name || user.name}</h1>
            {credit && credit.total > 0 && (
              <div className="mt-2 inline-flex items-center gap-2 bg-gold/15 border border-gold rounded-full px-4 py-1.5" data-testid="store-credit-badge">
                <Coins size={15} className="text-maroon"/>
                <span className="text-sm text-maroon font-medium">Store credit: {fmtINR(credit.total)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="btn-outline text-sm flex items-center gap-2" data-testid="ud-home"><BookOpen size={16}/> Home</Link>
            <button onClick={handleLogout} className="btn-outline text-sm flex items-center gap-2" data-testid="ud-logout">
              <LogOut size={16}/> Logout
            </button>
          </div>
        </div>

        {/* Profile — shown at top; email / phone one-time change */}
        <Section title="My Profile" icon={User} testid="ud-profile">
          <div className="bg-white border border-line p-5 max-w-lg space-y-3 text-sm">
            <div>
              <div className="text-[10px] text-ash uppercase tracking-widest">Name</div>
              <div className="font-medium">{d.profile?.name || user.name}</div>
            </div>
            <div className="flex items-center justify-between">
              <div><div className="text-[10px] text-ash uppercase tracking-widest">Email</div><div>{d.profile?.email}</div></div>
              {d.profile?.email_changed_once
                ? <span className="text-[10px] text-ash">Changed (one-time used)</span>
                : <button onClick={() => { setEdit("email"); setEditVal(d.profile?.email || ""); }} className="text-maroon text-xs underline" data-testid="ud-edit-email">Change once</button>}
            </div>
            <div className="flex items-center justify-between">
              <div><div className="text-[10px] text-ash uppercase tracking-widest">Phone</div><div>{d.profile?.phone || "—"}</div></div>
              {d.profile?.phone_changed_once
                ? <span className="text-[10px] text-ash">Changed (one-time used)</span>
                : <button onClick={() => { setEdit("phone"); setEditVal(d.profile?.phone || ""); }} className="text-maroon text-xs underline" data-testid="ud-edit-phone">Change once</button>}
            </div>
            {edit && (
              <div className="border-t border-line pt-3">
                <div className="text-xs text-ash mb-2">⚠ {edit === "email" ? "Email" : "Phone"} can be changed only ONCE in your lifetime. Make sure it's correct.</div>
                <div className="flex gap-2">
                  <input className="flex-1 border border-line px-3 py-2 bg-pearl" value={editVal}
                    onChange={(e) => setEditVal(edit === "phone" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value)}
                    maxLength={edit === "phone" ? 10 : 100} data-testid="ud-edit-value"/>
                  <button onClick={saveProfile} className="btn-maroon text-xs" data-testid="ud-edit-save">Save</button>
                  <button onClick={() => setEdit(null)} className="btn-outline text-xs">Cancel</button>
                </div>
              </div>
            )}
            <div className="border-t border-line pt-3">
              <button onClick={handleLogout} className="btn-outline text-xs flex items-center gap-2" data-testid="ud-profile-logout">
                <LogOut size={14}/> Logout
              </button>
            </div>
          </div>
        </Section>

        {/* Insights */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Insight icon={BadgeIndianRupee} label="Lifetime Value" value={fmtINR(d.ltv || 0)}/>
          <Insight icon={Star} label="Loyalty Points" value={d.loyalty_points ?? 0}/>
          <Insight icon={Gift} label="Referral Code" value={d.referral_code || "—"} sub="Share & earn"/>
          <Insight icon={BadgeIndianRupee} label="Extra Earnings" value={fmtINR(d.referral_earnings || 0)} sub="From referrals"/>
        </div>

        {/* Daily rates */}
        <section className="bg-maroon text-pearl p-5" data-testid="ud-rates">
          <div className="eyebrow text-gold-light mb-2">Today's Rates</div>
          <div className="flex flex-wrap gap-6 font-display text-xl">
            {rates.gold_24k > 0 && <span>24K ₹{rates.gold_24k}/g</span>}
            {rates.gold_22k > 0 && <span>22K ₹{rates.gold_22k}/g</span>}
            {rates.gold_18k > 0 && <span>18K ₹{rates.gold_18k}/g</span>}
            {rates.silver > 0 && <span>Silver ₹{rates.silver}/g</span>}
          </div>
        </section>

        {/* Budget matcher banner + quick links */}
        <section className="bg-gradient-to-r from-gold/20 to-maroon/10 border border-gold p-5 flex flex-wrap items-center justify-between gap-3" data-testid="ud-budget-banner">
          <div>
            <div className="font-display text-2xl text-maroon"><Sparkles size={18} className="inline mr-1"/>Smart Budget Match</div>
            <div className="text-xs text-ash">Enter your budget or grams, choose gold or silver — we match pieces to it.</div>
          </div>
          <Link to="/budget-match" className="btn-maroon text-sm" data-testid="ud-budget-btn">Try Budget Match</Link>
        </section>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Quick to="/wishlist" icon={Heart} label="My Wishlist"/>
          <Quick to="/cart" icon={ShoppingCart} label="My Cart"/>
          <Quick to="/astrology" icon={MessageCircle} label="Talk to Astrologer"/>
          <Quick to="/catalog" icon={ShoppingBag} label="Browse Jewellery"/>
        </div>

        {/* Purchase history */}
        <Section title="My Buying History" icon={ShoppingBag} testid="ud-orders">
          {(d.orders || []).length === 0 ? <Empty text="No purchases yet."/> : (
            <div className="bg-white border border-line overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-pearl border-b border-line"><tr>{["Date", "Item(s)", "Item ID(s)", "Amount", "Status"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
                <tbody>
                  {(d.orders || []).map((o) => (
                    <tr key={o.id} className="border-b border-line/60">
                      <td className="p-3 text-xs">{new Date(o.created_at).toLocaleDateString("en-GB")}</td>
                      <td className="p-3 text-xs">{(o.items || []).map((i) => i.name).join(", ")}</td>
                      <td className="p-3 text-[10px] text-ash">{(o.items || []).map((i) => (i.product_id || "").slice(0, 8).toUpperCase()).join(", ")}</td>
                      <td className="p-3 text-maroon font-medium">{fmtINR(o.total)}</td>
                      <td className="p-3 text-xs">{o.payment_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Karigar workflow tracking (karigar identity hidden) */}
        <Section title="My Custom Work (Karigar Status)" icon={Hammer} testid="ud-karigar">
          {(d.karigar_jobs || []).length === 0 ? <Empty text="No customization or redesign work in progress."/> : (
            <div className="grid lg:grid-cols-2 gap-4">
              {(d.karigar_jobs || []).map((j) => {
                const steps = KARIGAR_STEPS[j.job_type] || KARIGAR_STEPS.design;
                const curIdx = steps.indexOf(j.status);
                return (
                  <div key={j.id} className="bg-white border border-line p-4" data-testid={`ud-job-${j.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{j.category} · {j.grams}g {j.metal} {j.purity}</div>
                        <div className="text-[10px] text-ash uppercase tracking-widest mt-0.5">{j.job_type === "redesign" ? "Redesign" : "New Design"}</div>
                      </div>
                      <span className="bg-gold/20 text-maroon px-2 py-0.5 text-[10px] uppercase tracking-widest">{j.status}</span>
                    </div>
                    <div className="flex items-center mt-4">
                      {steps.map((s, i) => (
                        <React.Fragment key={s}>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] shrink-0 ${i <= curIdx ? "bg-maroon text-pearl" : "bg-line text-ash"}`}>{i + 1}</div>
                          {i < steps.length - 1 && <div className={`h-0.5 flex-1 ${i < curIdx ? "bg-maroon" : "bg-line"}`}/>}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="flex justify-between text-[8px] uppercase tracking-wider text-ash mt-1">
                      <span>{steps[0]}</span><span>{steps[steps.length - 1]}</span>
                    </div>
                    <div className="mt-3 space-y-1 max-h-24 overflow-y-auto">
                      {(j.updates || []).slice().reverse().map((u, i) => (
                        <div key={i} className="text-[11px] text-ash">✓ {u.status} — {new Date(u.at).toLocaleString()}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Swayambhu Gold Scheme */}
        <Section title="My Swayambhu Gold Scheme" icon={Coins} testid="ud-gold-scheme">
          {goldScheme.length === 0 ? (
            <div className="bg-white border border-line p-5 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-ash">Invest any amount each month (min ₹5,000), grow your gold, and get free making at the end.</div>
              <Link to="/gold-scheme" className="btn-maroon text-sm">Join the scheme</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {goldScheme.map((s) => {
                const paid = s.installments_paid || 0, total = s.duration_months || 12;
                const pct = Math.round((paid / total) * 100);
                return (
                  <div key={s.id} className="bg-white border border-line p-5" data-testid={`ud-scheme-${s.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="font-display text-xl text-maroon">{s.scheme_name}</div>
                      {s.status === "completed" || s.making_free
                        ? <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Completed · Making free</span>
                        : <Link to="/gold-scheme" className="text-xs text-maroon underline">Pay next instalment →</Link>}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <MiniStat label="Flexible / mo" value={`min ${fmtINR(5000)}`}/>
                      <MiniStat label="Amount paid" value={fmtINR(s.total_paid || 0)}/>
                      <MiniStat label="Gold earned" value={`${(s.total_grams || 0).toFixed(3)} g`}/>
                      <MiniStat label="Instalments" value={`${paid} / ${total}`}/>
                    </div>
                    <div className="h-2 bg-pearl rounded-full overflow-hidden mb-3"><div className="h-full bg-gold" style={{ width: `${pct}%` }}/></div>
                    {(s.installments || []).length > 0 && (
                      <div className="border border-line overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-pearl border-b border-line"><tr>{["#", "Paid on", "Amount", "Rate/g", "Gold (g)"].map((h) => <th key={h} className="text-left p-2 eyebrow text-[9px]">{h}</th>)}</tr></thead>
                          <tbody>
                            {s.installments.map((it) => (
                              <tr key={it.payment_id} className="border-b border-line/60">
                                <td className="p-2">{it.month_no}</td>
                                <td className="p-2">{new Date(it.paid_at).toLocaleDateString()}</td>
                                <td className="p-2">{fmtINR(it.amount)}</td>
                                <td className="p-2">{fmtINR(it.rate_per_gram)}</td>
                                <td className="p-2">{(it.grams || 0).toFixed(3)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Schemes + rate locks + exchange, side by side */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Section title="My Schemes" icon={Star} slim testid="ud-schemes">
            {(d.schemes || []).length === 0 ? <Empty text="No scheme enrollments."/> : (d.schemes || []).map((s) => (
              <div key={s.id} className="bg-white border border-line p-3 mb-2 text-sm">
                <div className="font-medium">{s.scheme_name}</div>
                <div className="text-xs text-ash">{s.installments_paid}/{s.duration_months} months · {fmtINR(s.total_paid)} paid</div>
              </div>
            ))}
          </Section>
          <Section title="My Rate Locks" icon={BadgeIndianRupee} slim testid="ud-locks">
            {(d.rate_locks || []).length === 0 ? <Empty text="No rate-lock tokens."/> : (d.rate_locks || []).map((b) => (
              <div key={b.id} className="bg-white border border-line p-3 mb-2 text-sm">
                <div className="font-medium">₹{b.locked_rate_per_g}/g · {b.desired_purity}</div>
                <div className="text-xs text-ash">{b.payment_status} {b.transaction_id && `· ${b.transaction_id}`}</div>
              </div>
            ))}
          </Section>
          <Section title="Exchange History" icon={RefreshCcw} slim testid="ud-exchange">
            {exchanges.length === 0 ? <Empty text="No gold exchanges yet."/> : exchanges.map((o) => (
              <div key={o.id} className="bg-white border border-line p-3 mb-2 text-sm">
                <div className="font-medium">{fmtINR(o.total)}</div>
                <div className="text-xs text-ash">{new Date(o.created_at).toLocaleDateString("en-GB")}</div>
              </div>
            ))}
          </Section>
        </div>

        {/* Personal catalogs generated by the user */}
        <Section title="My Personal Catalogs" icon={FileText} testid="ud-catalogs">
          <p className="text-xs text-ash mb-3">Browse the <Link to="/catalog" className="text-maroon underline">catalog</Link>, tap "Catalog" on pieces you like, then Generate PDF from the floating builder — the PDF is marked as generated by you.</p>
          {(d.catalogs || []).length === 0 ? <Empty text="No catalogs generated yet."/> : (
            <div className="grid lg:grid-cols-3 gap-3">
              {(d.catalogs || []).map((c) => (
                <div key={c.id} className="bg-white border border-line p-3 text-sm">
                  <div className="font-medium">Catalog {c.id.slice(0, 8).toUpperCase()}</div>
                  <div className="text-xs text-ash">{new Date(c.created_at).toLocaleDateString("en-GB")} · {fmtINR(c.total_value || 0)}</div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Blogs + stores */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="From Our Blog" icon={BookOpen} slim testid="ud-blogs">
            {blogs.map((b) => (
              <Link key={b.id} to={`/blog/${b.slug}`} className="block bg-white border border-line p-3 mb-2 hover:border-maroon">
                <div className="font-medium text-sm">{b.title}</div>
                <div className="text-xs text-ash">{new Date(b.created_at).toLocaleDateString("en-GB")}</div>
              </Link>
            ))}
            {blogs.length === 0 && <Empty text="No blogs yet."/>}
          </Section>
          <Section title="Our Stores" icon={MapPin} slim testid="ud-stores">
            {stores.map((s) => (
              <div key={s.id} className="bg-white border border-line p-3 mb-2 text-sm">
                <div className="font-medium">{s.name}</div>
                <div className="text-xs text-ash">{s.address} · {s.phone}</div>
              </div>
            ))}
            {stores.length === 0 && <Empty text="Store list coming soon."/>}
          </Section>
        </div>

      </div>
    </StorefrontLayout>
  );
}

const Insight = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white border border-line p-4">
    <div className="flex items-center justify-between"><div className="eyebrow text-[9px]">{label}</div><Icon size={14} className="text-gold"/></div>
    <div className="font-display text-2xl text-maroon mt-1 break-all">{value}</div>
    {sub && <div className="text-[10px] text-ash">{sub}</div>}
  </div>
);

const Quick = ({ to, icon: Icon, label }) => (
  <Link to={to} className="bg-white border border-line p-4 flex items-center gap-2 hover:border-maroon text-sm" data-testid={`ud-quick-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <Icon size={16} className="text-maroon"/> {label}
  </Link>
);

const MiniStat = ({ label, value }) => (
  <div className="bg-pearl border border-line p-2.5">
    <div className="eyebrow text-[9px]">{label}</div>
    <div className="font-display text-lg text-maroon">{value}</div>
  </div>
);

const Section = ({ title, icon: Icon, children, slim, testid }) => (
  <section data-testid={testid}>
    <div className={`flex items-center gap-2 ${slim ? "mb-2" : "mb-4"}`}>
      <Icon size={16} className="text-gold"/>
      <h2 className="font-display text-2xl text-maroon">{title}</h2>
    </div>
    {children}
  </section>
);

const Empty = ({ text }) => <div className="text-xs text-ash bg-white border border-line p-4">{text}</div>;
